// Moving things in the Robing Room, the Conference Room and the Marshal's Office (world.json `fx`): the fire in the
// conference room grate, the handshake every justice gives every other before the bench and before conference, and the
// Marshal's monitor wall. Imported for its side effect by the screens that live in these rooms. Like fx.ts, everything
// is a pure function of the clock, and under reduced motion each draws a still rest frame.
import { registerFx, type FxContext } from './fx';
import type { FxEntry } from './assets';
import { hashStr } from './life';

const C = {
  ol: '#160f1a', r0: '#1b1822', r1: '#2a2634', r2: '#3c3749', r3: '#565068',
  v2: '#8f1c28', v3: '#ba343c', b2: '#c59b3c', b3: '#eed47c', lw: '#fff4c4', lr: '#ff4b3b',
  sl0: '#d9a382', sl1: '#efc4a2', sm0: '#a86b46', sm1: '#c4865b', sd0: '#4a2a1a', sd1: '#6e4029',
  n0: '#141b2e', g2: '#3b6449', g3: '#5f8c5a', s1: '#9bb8d3', s2: '#d8e4ea', h4: '#bb683c',
};

function px(ctx: CanvasRenderingContext2D, x: number, y: number, c: string, w = 1, h = 1): void {
  ctx.fillStyle = c;
  ctx.fillRect(Math.round(x), Math.round(y), w, h);
}

type Extra = FxEntry & { a?: string; b?: string };

/** Flames in a grate: (x, y) the middle of the hearth floor, w its width. Tongues rise and fall out of step. */
export function drawFire(ctx: CanvasRenderingContext2D, e: FxEntry, c: FxContext, h: number): void {
  const w = e.w ?? 20;
  const t = c.animate ? c.now : 0;
  const x0 = e.x - Math.floor(w / 2);
  for (let i = 0; i < w; i += 2) {
    const s = hashStr(`${h}:${i}`);
    const centre = 1 - Math.abs(i - w / 2) / (w / 2);
    const flick = c.animate ? (Math.sin(t / (90 + (s % 70)) + (s % 13)) + Math.sin(t / 230 + i)) / 2 : 0.3;
    const tall = Math.max(1, Math.round(1 + centre * 5 + flick * 2));
    for (let k = 0; k < tall; k += 1) {
      const col = k === 0 ? C.v3 : k < tall - 2 ? C.lr : k < tall - 1 ? C.b3 : C.lw;
      px(ctx, x0 + i + (k > 2 && flick > 0.4 ? 1 : 0), e.y - k, col, 2, 1);
    }
  }
  // embers drifting up
  if (c.animate) {
    for (let k = 0; k < 3; k += 1) {
      const a = ((t + k * 433 + (h % 900)) % 1300) / 1300;
      px(ctx, e.x - 6 + ((hashStr(`${h}:e${k}:${Math.floor((t + k * 433) / 1300)}`) % 12)), e.y - 6 - a * 8, a < 0.6 ? C.b3 : C.v3);
    }
  }
}

function skin(k: string | undefined): [string, string] {
  if (k === 'd') return [C.sd1, C.sd0];
  if (k === 'm') return [C.sm1, C.sm0];
  return [C.sl1, C.sl0];
}

/** Two justices in profile shaking hands: forearms in black robe sleeves meet at (x, y - 19), feet row y.
 *  The clasp pumps a pixel up and down, three shakes, a pause. */
export function drawHandshake(ctx: CanvasRenderingContext2D, e: FxEntry, c: FxContext, h: number): void {
  const ex = e as Extra;
  const cycle = 3600 + (h % 900);
  const p = c.animate ? ((c.now + h) % cycle) / cycle : 1;
  const pump = p < 0.45 ? (Math.floor(p * 18) % 2 === 0 ? 0 : 1) : 0;
  const y = e.y - 19 + pump;
  const x = e.x;
  const [a1, a0] = skin(ex.a);
  const [b1] = skin(ex.b);
  // sleeves reaching forward from each body
  px(ctx, x - 5, y - 1, C.ol, 4, 4); px(ctx, x - 5, y, C.r2, 4, 2);
  px(ctx, x + 2, y - 1, C.ol, 4, 4); px(ctx, x + 2, y, C.r2, 4, 2);
  // the clasped hands
  px(ctx, x - 2, y - 1, C.ol, 5, 4);
  px(ctx, x - 1, y, a1, 2, 2); px(ctx, x + 1, y, b1, 1, 2); px(ctx, x - 1, y + 1, a0);
}

/** A status monitor: dim navy, rows of status lines that redraw now and then, a cursor that blinks, an occasional
 *  roll bar sweeping down the tube. */
export function drawMonitor(ctx: CanvasRenderingContext2D, e: FxEntry, c: FxContext, h: number): void {
  const w = e.w ?? 16; const hh = e.h ?? 10;
  const t = c.animate ? c.now : 0;
  const page = Math.floor((t + (h % 5000)) / (2600 + (h % 1700)));
  px(ctx, e.x, e.y, C.n0, w, hh);
  for (let r = 1; r < hh - 1; r += 3) {
    const s = hashStr(`${h}:${page}:${r}`);
    const len = 2 + (s % Math.max(2, w - 4));
    px(ctx, e.x + 1, e.y + r, s % 7 === 0 ? C.v3 : C.g2, len, 1);
    if (s % 5 === 0) px(ctx, e.x + 1 + len + 1, e.y + r, C.g3, 2, 1);
  }
  if (c.animate && Math.floor(t / 500) % 2 === 0) px(ctx, e.x + w - 3, e.y + hh - 2, C.g3, 2, 1);
  if (c.animate) {
    const roll = ((t + (h % 3000)) % 4200) / 4200;
    ctx.globalAlpha = 0.18;
    px(ctx, e.x, e.y + Math.floor(roll * hh), C.s2, w, 1);
    ctx.globalAlpha = 1;
  }
}

registerFx('fire', drawFire, 'ground');
registerFx('handshake', drawHandshake, 'tall');
registerFx('monitor', drawMonitor, 'ground');
